import {
  BatchWriteItemCommand,
  DeleteItemCommand,
  DynamoDBClient,
  QueryCommand,
  UpdateItemCommand,
  type QueryCommandInput,
  type QueryCommandOutput
} from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { logger } from 'src/logger';
import { CognitoService } from 'src/services/aws/CognitoService';
import { RankingWeights, RecentEntityType, RecentsConstants } from './RecentsConstants';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
// Local `dev` stage maps to the alpha DynamoDB table.
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const TABLE_NAME = `finsuite_user_recents_${normalizedStage}`;

// ── Change listeners ───────────────────────────────────────────────────
// Lightweight pub/sub so `useRecents` can re-fetch when a write lands.
type ChangeListener = (entityType: RecentEntityType) => void;
const changeListeners = new Set<ChangeListener>();

/** Subscribe to recents changes. Returns an unsubscribe function. */
export const onRecentsChange = (listener: ChangeListener): (() => void) => {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
};

const notifyChange = (entityType: RecentEntityType): void => {
  changeListeners.forEach((fn) => fn(entityType));
};

/** DynamoDB BatchWriteItem hard limit per request. */
const BATCH_WRITE_LIMIT = 25;

/** Upper bound on retries for `UnprocessedItems` from BatchWriteItem. */
const BATCH_WRITE_MAX_RETRIES = 3;

/**
 * Encode an entity type and id into the DynamoDB sort key.
 *
 * Exposed so tests can assert against it, but callers should not need this —
 * they pass `(type, id)` into the service and the service handles encoding.
 */
export const buildSortKey = (type: RecentEntityType, entityId: string): string => `${type}#${entityId}`;

/** Raw entry as read from DynamoDB. NOT hydrated against any catalog. */
export interface RecentEntry {
  entityType: RecentEntityType;
  entityId: string;
  visitedAt: string;
  visitCount: number;
}

/** Options for `recordVisit`. Type-specific knobs live here so the signature stays simple. */
export interface RecordVisitOptions {
  /** APP-only: did the user click an external-link app (opened via `window.open`)? Gated by `APP_TRACK_EXTERNAL`. */
  isExternal?: boolean;
}

/**
 * Compute the hybrid recency + frequency score used to rank recents.
 *
 * Exported for unit tests and so the hook can rank entries in memory
 * after hydration, without shipping raw visit data back through the service.
 */
export function hybridScore(visitedAt: string, visitCount: number, weights: RankingWeights, now: number = Date.now()): number {
  const ageMs = now - new Date(visitedAt).getTime();
  const ageDays = Math.max(0, ageMs / (1000 * 60 * 60 * 24));

  const recencyScore = Math.pow(0.5, ageDays / RecentsConstants.RECENCY_HALF_LIFE_DAYS);
  const frequencyScore = Math.log1p(Math.max(0, visitCount)) / Math.log1p(RecentsConstants.FREQUENCY_NORMALIZATION_ANCHOR);

  return weights.recency * recencyScore + weights.frequency * frequencyScore;
}

/**
 * Typed DynamoDB-backed service for the User Recents feature.
 *
 * Design notes (see `analysis-landing-page-redesign/recents-tech-spec.md`):
 *
 * - Display name / URL / logo for an entry are NEVER stored. A per-type
 *   hydrator (in `src/services/recents/hydrators/`) resolves `entity_id` to
 *   something renderable at render time, sourced from the authoritative
 *   source of truth (`parsedApplications`, `PROFILE_DROPDOWN_ITEMS`, etc.).
 *   This sidesteps rename-staleness and makes deletions filter out
 *   automatically.
 *
 * - Every method fails gracefully. Reads return `[]` / `{}`, writes are
 *   fire-and-forget. All failures are logged via KatalLogger so operations
 *   can alarm on error rates without any UI feedback.
 *
 *
 * - All reads paginate on `LastEvaluatedKey` — the 1 MB Query boundary would
 *   otherwise silently lose items from power users.
 *
 * - `clearAllOfType` handles `UnprocessedItems` from `BatchWriteItem` with
 *   bounded retries to avoid silent partial-delete.
 */
export class RecentsService {
  private static client: DynamoDBClient | null = null;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  // ── Writes ───────────────────────────────────────────────────────────

  /**
   * Record (or upsert) a visit.
   *
   * Fire-and-forget: callers MUST NOT await this. Never throws; logs on
   * failure. Respects `RecentsConstants.APP_TRACK_EXTERNAL` for APP visits.
   *
   * Idempotent per `(sub, type, id)`: `UpdateItem` upserts `visited_at` /
   * `user_alias` / `entity_type` / `entity_id` / `ttl` and atomically
   * increments `visit_count` via `ADD`. Concurrent calls (multiple tabs)
   * converge correctly — `visit_count` is commutative and `visited_at`
   * follows last-write-wins.
   */
  public static recordVisit(
    cognitoSub: string,
    userAlias: string,
    entityType: RecentEntityType,
    entityId: string,
    options: RecordVisitOptions = {}
  ): void {
    if (!cognitoSub || !entityId) {
      // Defensive: surfaces that embed this service render only after auth
      // completes, so this should never fire in practice. Warn so it shows
      // up in logs if it does.
      logger?.warn?.('RecentsService.recordVisit skipped — missing identity or entityId', {
        entityType,
        hasCognitoSub: Boolean(cognitoSub),
        hasEntityId: Boolean(entityId)
      });
      return;
    }

    if (entityType === RecentEntityType.APP && options.isExternal && !RecentsConstants.APP_TRACK_EXTERNAL) {
      return;
    }

    const now = new Date();
    const nowIso = now.toISOString();
    const ttlDays = RecentsConstants.TTL_DAYS_BY_TYPE[entityType];
    const ttlEpochSeconds = Math.floor(now.getTime() / 1000) + ttlDays * 24 * 60 * 60;

    const client = this.getClient();

    // Fire-and-forget — errors are logged, never surfaced to the caller.
    void client
      .send(
        new UpdateItemCommand({
          TableName: TABLE_NAME,
          Key: marshall({ cognito_sub: cognitoSub, sk: buildSortKey(entityType, entityId) }),
          UpdateExpression: 'SET visited_at = :now, user_alias = :alias, entity_type = :etype, entity_id = :eid, #ttl = :ttl ADD visit_count :one',
          ExpressionAttributeNames: {
            // `ttl` is a DynamoDB reserved word; alias it via ExpressionAttributeNames.
            '#ttl': 'ttl'
          },
          ExpressionAttributeValues: marshall({
            ':now': nowIso,
            ':alias': userAlias ?? '',
            ':etype': entityType,
            ':eid': entityId,
            ':ttl': ttlEpochSeconds,
            ':one': 1
          })
        })
      )
      .then(() => {
        notifyChange(entityType);
      })
      .catch((err: unknown) => {
        logger?.error?.('RecentsService.recordVisit failed', {
          operation: 'recordVisit',
          entityType,
          entityId,
          errorName: (err as Error)?.name,
          errorMessage: (err as Error)?.message
        });
      });
  }

  // ── Reads ────────────────────────────────────────────────────────────

  /**
   * Fetch raw recent entries for one entity type.
   *
   * Returns `[]` on any failure (logged). Paginates through `LastEvaluatedKey`
   * so power users with large recents aren't silently truncated at the 1 MB
   * Query boundary. The hook (`useRecents`) is responsible for hydrating
   * entries against a catalog, ranking, and capping.
   */
  public static async getRecentsOfType(cognitoSub: string, entityType: RecentEntityType): Promise<RecentEntry[]> {
    if (!cognitoSub) {
      logger?.warn?.('RecentsService.getRecentsOfType skipped — missing CognitoSub', { entityType });
      return [];
    }

    try {
      const client = this.getClient();
      const prefix = `${entityType}#`;

      const allItems: Record<string, unknown>[] = [];
      let exclusiveStartKey: QueryCommandInput['ExclusiveStartKey'] = undefined;

      do {
        const result: QueryCommandOutput = await client.send(
          new QueryCommand({
            TableName: TABLE_NAME,
            KeyConditionExpression: 'cognito_sub = :sub AND begins_with(sk, :prefix)',
            ExpressionAttributeValues: marshall({ ':sub': cognitoSub, ':prefix': prefix }),
            ExclusiveStartKey: exclusiveStartKey
          })
        );
        for (const item of result.Items ?? []) {
          allItems.push(unmarshall(item));
        }
        exclusiveStartKey = result.LastEvaluatedKey;
      } while (exclusiveStartKey);

      return allItems.map((i) => ({
        entityType: (i.entity_type as RecentEntityType) ?? entityType,
        entityId: i.entity_id as string,
        visitedAt: i.visited_at as string,
        visitCount: (i.visit_count as number) ?? 0
      }));
    } catch (err: unknown) {
      logger?.error?.('RecentsService.getRecentsOfType failed', {
        operation: 'getRecentsOfType',
        entityType,
        errorName: (err as Error)?.name,
        errorMessage: (err as Error)?.message
      });
      return [];
    }
  }

  /**
   * Fetch raw recents for every type in a single `Query` (no sort-key prefix).
   * Groups by `entity_type` on the way out.
   *
   * Returns an empty object on failure (logged). Used by surfaces that need
   * multiple types at once, to avoid N round-trips.
   */
  public static async getAllRecents(cognitoSub: string): Promise<Partial<Record<RecentEntityType, RecentEntry[]>>> {
    if (!cognitoSub) {
      logger?.warn?.('RecentsService.getAllRecents skipped — missing CognitoSub');
      return {};
    }

    try {
      const client = this.getClient();

      const allItems: Record<string, unknown>[] = [];
      let exclusiveStartKey: QueryCommandInput['ExclusiveStartKey'] = undefined;

      do {
        const result: QueryCommandOutput = await client.send(
          new QueryCommand({
            TableName: TABLE_NAME,
            KeyConditionExpression: 'cognito_sub = :sub',
            ExpressionAttributeValues: marshall({ ':sub': cognitoSub }),
            ExclusiveStartKey: exclusiveStartKey
          })
        );
        for (const item of result.Items ?? []) {
          allItems.push(unmarshall(item));
        }
        exclusiveStartKey = result.LastEvaluatedKey;
      } while (exclusiveStartKey);

      const grouped: Partial<Record<RecentEntityType, RecentEntry[]>> = {};
      for (const i of allItems) {
        const etype = i.entity_type as RecentEntityType;
        // Defensively skip rows whose type isn't in the enum (e.g. left over from a removed type).
        if (!Object.values(RecentEntityType).includes(etype)) {
          continue;
        }
        const entry: RecentEntry = {
          entityType: etype,
          entityId: i.entity_id as string,
          visitedAt: i.visited_at as string,
          visitCount: (i.visit_count as number) ?? 0
        };
        (grouped[etype] ??= []).push(entry);
      }
      return grouped;
    } catch (err: unknown) {
      logger?.error?.('RecentsService.getAllRecents failed', {
        operation: 'getAllRecents',
        errorName: (err as Error)?.name,
        errorMessage: (err as Error)?.message
      });
      return {};
    }
  }

  // ── Deletes (API only this phase — no UI surface yet) ────────────────

  /** Remove a single recent entry. Silent on failure (logged). */
  public static async removeRecent(cognitoSub: string, entityType: RecentEntityType, entityId: string): Promise<void> {
    if (!cognitoSub || !entityId) return;

    try {
      const client = this.getClient();
      await client.send(
        new DeleteItemCommand({
          TableName: TABLE_NAME,
          Key: marshall({ cognito_sub: cognitoSub, sk: buildSortKey(entityType, entityId) })
        })
      );
    } catch (err: unknown) {
      logger?.error?.('RecentsService.removeRecent failed', {
        operation: 'removeRecent',
        entityType,
        entityId,
        errorName: (err as Error)?.name,
        errorMessage: (err as Error)?.message
      });
    }
  }

  /**
   * Remove every recent of one entity type.
   *
   * Implementation: paginated `Query` with `begins_with(sk, <TYPE>#)` to
   * collect sort keys, then chunk into 25-item `BatchWriteItem` batches
   * (DDB's per-request limit). Retries on `UnprocessedItems` up to
   * `BATCH_WRITE_MAX_RETRIES`; logs a warning with the unprocessed count if
   * any remain after retries.
   */
  public static async clearAllOfType(cognitoSub: string, entityType: RecentEntityType): Promise<void> {
    if (!cognitoSub) return;

    const client = this.getClient();

    try {
      const prefix = `${entityType}#`;
      const sks: string[] = [];
      let exclusiveStartKey: QueryCommandInput['ExclusiveStartKey'] = undefined;

      // Collect every sort key for this (user, type). Paginated.
      do {
        const result: QueryCommandOutput = await client.send(
          new QueryCommand({
            TableName: TABLE_NAME,
            KeyConditionExpression: 'cognito_sub = :sub AND begins_with(sk, :prefix)',
            ExpressionAttributeValues: marshall({ ':sub': cognitoSub, ':prefix': prefix }),
            ProjectionExpression: 'sk',
            ExclusiveStartKey: exclusiveStartKey
          })
        );
        for (const item of result.Items ?? []) {
          sks.push(unmarshall(item).sk as string);
        }
        exclusiveStartKey = result.LastEvaluatedKey;
      } while (exclusiveStartKey);

      if (sks.length === 0) return;

      // Chunk + delete with UnprocessedItems handling.
      for (let i = 0; i < sks.length; i += BATCH_WRITE_LIMIT) {
        const batch = sks.slice(i, i + BATCH_WRITE_LIMIT);
        let requestItems: Record<string, { DeleteRequest: { Key: Record<string, unknown> } }[]> = {
          [TABLE_NAME]: batch.map((sk) => ({
            DeleteRequest: { Key: marshall({ cognito_sub: cognitoSub, sk }) }
          }))
        };

        for (let attempt = 0; attempt <= BATCH_WRITE_MAX_RETRIES; attempt++) {
          try {
            const response = await client.send(new BatchWriteItemCommand({ RequestItems: requestItems as never }));
            const unprocessed = response.UnprocessedItems ?? {};
            const unprocessedForTable = unprocessed[TABLE_NAME];
            if (!unprocessedForTable || unprocessedForTable.length === 0) {
              break; // Batch fully processed.
            }
            if (attempt === BATCH_WRITE_MAX_RETRIES) {
              logger?.warn?.('RecentsService.clearAllOfType unprocessed items', {
                operation: 'clearAllOfType',
                entityType,
                unprocessedCount: unprocessedForTable.length
              });
              break;
            }
            // Exponential backoff: 50ms, 100ms, 200ms.
            await new Promise((resolve) => setTimeout(resolve, 50 * Math.pow(2, attempt)));
            requestItems = { [TABLE_NAME]: unprocessedForTable as never };
          } catch (err: unknown) {
            logger?.error?.('RecentsService.clearAllOfType batch failed', {
              operation: 'clearAllOfType',
              entityType,
              batchSize: batch.length,
              errorName: (err as Error)?.name,
              errorMessage: (err as Error)?.message
            });
            break; // Move on to the next batch; don't let one failure cascade.
          }
        }
      }
    } catch (err: unknown) {
      logger?.error?.('RecentsService.clearAllOfType failed', {
        operation: 'clearAllOfType',
        entityType,
        errorName: (err as Error)?.name,
        errorMessage: (err as Error)?.message
      });
    }
  }
}
